"use client";

import { Button, Text, ToggleButton } from "@softmax/design-system";
import { colors, spacing } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { MediaVideoInput } from "./media-video-input";
import { MediaImageInput } from "./media-image-input";
import { MediaTrayRow } from "./media-tray-row";
import type { MediaTrayItem } from "./media-records";
import { useEditorReorder } from "../use-editor-reorder";

type MediaTrayViewProps = Readonly<{
  items: readonly MediaTrayItem[];
  disabled: boolean;
  maxItems: number;
  altTextMax: number;
  uploadingImage: boolean;
  uploadingPoster: boolean;
  videoUrl: string;
  posterReady: boolean;
  videoError: string | null;
  uploadError: string | null;
  onRetryUpload: () => void;
  showLiveReplay: boolean;
  onImageFile: (file: File) => void;
  onPosterFile: (file: File) => void;
  onVideoUrlChange: (value: string) => void;
  onAddVideo: () => void;
  onAltChange: ({
    index,
    value,
  }: Readonly<{ index: number; value: string }>) => void;
  onRemove: (index: number) => void;
  onReorder: ({ from, to }: Readonly<{ from: number; to: number }>) => void;
  onLiveReplayChange: (show: boolean) => void;
}>;

export function MediaTrayView(props: MediaTrayViewProps) {
  const reorder = useEditorReorder({
    count: props.items.length,
    disabled: props.disabled,
    onReorder: props.onReorder,
  });
  const uploading = props.uploadingImage || props.uploadingPoster;
  const atCap =
    props.items.length + (props.uploadingImage ? 1 : 0) >= props.maxItems;

  return (
    <div {...stylex.props(styles.tray)}>
      {props.items.length === 0 && (
        <Text size="small" tone="muted">
          No custom media yet. Add an image or video to replace the live default
          above.
        </Text>
      )}
      {props.uploadError && (
        <div role="alert">
          <Text size="small" tone="danger">
            {props.uploadError}
          </Text>
          <Button
            size="sm"
            disabled={props.disabled || uploading || atCap}
            onClick={props.onRetryUpload}
          >
            Retry upload
          </Button>
        </div>
      )}
      <ol {...stylex.props(styles.list)}>
        {props.items.map((item, index) => (
          <MediaTrayRow
            key={index}
            item={item}
            index={index}
            disabled={props.disabled}
            altTextMax={props.altTextMax}
            reorder={reorder}
            onAltChange={props.onAltChange}
            onRemove={props.onRemove}
          />
        ))}
      </ol>
      <MediaImageInput
        itemCount={props.items.length}
        maxItems={props.maxItems}
        disabled={props.disabled || atCap || uploading}
        uploadingImage={props.uploadingImage}
        onImageFile={props.onImageFile}
      />
      <MediaVideoInput
        disabled={props.disabled}
        uploading={uploading}
        atMediaLimit={atCap}
        uploadingPoster={props.uploadingPoster}
        videoUrl={props.videoUrl}
        posterReady={props.posterReady}
        videoError={props.videoError}
        onPosterFile={props.onPosterFile}
        onVideoUrlChange={props.onVideoUrlChange}
        onAddVideo={props.onAddVideo}
      />
      <ToggleButton
        disabled={props.disabled}
        size="sm"
        pressed={props.showLiveReplay}
        onPressedChange={props.onLiveReplayChange}
      >
        Show live replay alongside my media
      </ToggleButton>
    </div>
  );
}

const styles = stylex.create({
  tray: {
    display: "flex",
    flexDirection: "column",
    gap: spacing.space12,
    backgroundColor: colors.surface,
    padding: spacing.space12,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: spacing.space8,
    margin: 0,
    padding: 0,
    listStyle: "none",
  },
});
